import { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Typography
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { State, StateEvent, StateNode } from '@sierralabs/workflow';

import { FileDto } from '../../../documents/Document.interface';
import {
  CreateNewFileVersionConfigParams,
  CreateNewFileVersionConfigTaskContextData,
  PublishEventParams,
  TaskContext
} from '../../../tasks/Task.machine';
import { useFindLatestTrainingVersions } from '../../../training/Training.hooks';
import { PublishEventPlugin } from '../services/PublishEventPlugin';
import {
  TransitionConditionConfiguratorProps,
  TransitionConditionDisplayProps,
  TransitionConditionPlugin
} from '../TransitionConditionPlugin';

function findTargetStateNode(event: StateEvent) {
  for (const transition of event.transitions) {
    const stateNode = event.stateNode.parent?.states.find(stateNode => {
      // match target state node but not the stateNode of this event
      return (
        stateNode.path !== event.stateNode.path &&
        stateNode.path === transition.target
      );
    });
    if (stateNode) {
      return stateNode;
    }
  }
}

function findCreateNewFileVersionEvent(targetStateNode: StateNode) {
  // check if the target state node has a 'PublishEvent' workflow action
  const invokes = !targetStateNode
    ? []
    : Array.isArray(targetStateNode.invoke)
    ? targetStateNode.invoke
    : targetStateNode.invoke
    ? [targetStateNode.invoke]
    : [];

  // find 'file.createNewVersion' from the workflow action
  return invokes.find((invoke): invoke is PublishEventParams => {
    return (
      invoke.src === PublishEventPlugin.src &&
      invoke.data &&
      (invoke as PublishEventParams).data.events?.some(
        event => event.event === 'file.createNewVersion'
      )
    );
  });
}

const CreateNewFileVersionConfigConfigurator: React.FC<
  TransitionConditionConfiguratorProps
> = props => {
  const { taskType, event, condition, onChange } = props;
  if (!condition.params) condition.params = {};
  const params: CreateNewFileVersionConfigParams = condition.params;
  // only allow this plugin for subTask workflows (no taskType)
  // and File entities (taskType is 'File')
  const invalidEntity = taskType && taskType !== 'File';
  const targetStateNode = findTargetStateNode(event);
  // invalid setup if a taskType is set for the workflow
  // but the targetStateNode does not have the PublishEvent configured correctly
  const invalidSetup =
    taskType &&
    targetStateNode &&
    !findCreateNewFileVersionEvent(targetStateNode);

  const update = (params: CreateNewFileVersionConfigParams) => {
    const updatedInvoke = { ...condition, params };
    onChange?.(updatedInvoke);
  };

  return (
    <Box mt={2}>
      <FormControlLabel
        label='Allow optional Training updates'
        control={
          <Checkbox
            color='primary'
            checked={params.allowOptionalTrainingUpdates || false}
            onChange={(_, checked) => {
              update({ ...params, allowOptionalTrainingUpdates: checked });
            }}
          />
        }
      />
      <FormHelperText>
        Show a dialog prompt to allow to skip updating related Training to a new
        version.
      </FormHelperText>

      {invalidEntity ? (
        <Box mt={2}>
          <Alert severity='warning'>
            <AlertTitle>Invalid Entity</AlertTitle>
            This Workflow action is only available for SubTask Workflows and the{' '}
            <strong>File</strong> entity Workflow. The dialog prompt will not
            show during transitions.
          </Alert>
        </Box>
      ) : invalidSetup ? (
        <Box mt={2}>
          <Alert severity='warning'>
            <AlertTitle>Missing Publish Event Configuration</AlertTitle>A{' '}
            <strong>{PublishEventPlugin.name}</strong> Workflow action with a{' '}
            <strong>Create New File Version</strong> event configuration is
            required to be configured on the target state{' '}
            {targetStateNode && (
              <>
                (<strong>{targetStateNode.name}</strong>){' '}
              </>
            )}
            for this transition condition to work correctly. Please reconfigure
            the target state and add the correct Workflow action and event.
          </Alert>
        </Box>
      ) : null}
    </Box>
  );
};

const CreateNewFileVersionConfigDisplay: React.FC<
  TransitionConditionDisplayProps
> = () => {
  return null;
};

const StyledDialogContent = styled(DialogContent)`
  .MuiTypography-root,
  .MuiAlert-root {
    &:not(:last-child) {
      margin-bottom: 12px;
    }
  }
`;

export interface CreateNewFileVersionDialogProps {
  open: boolean;
  file: FileDto | undefined;
  onCancel?: () => void;
  onSuccess?: (data: CreateNewFileVersionConfigTaskContextData) => void;
}

const CreateNewFileVersionDialog: React.FC<
  CreateNewFileVersionDialogProps
> = props => {
  const { open, file, onCancel, onSuccess } = props;
  const [skipTrainingUpdates, setSkipTrainingUpdates] = useState(false);
  const { trainingVersions, findTrainingVersions } =
    useFindLatestTrainingVersions();
  const fileId = file?.id;

  useEffect(() => {
    fileId && findTrainingVersions({ fileId });
  }, [fileId, findTrainingVersions]);

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>Update Training Version</DialogTitle>
      <StyledDialogContent>
        <Typography>
          This document is linked to one or more Trainings. Disable the checkbox
          to not assign those Trainings.
        </Typography>

        {trainingVersions && trainingVersions.length > 0 && (
          <Typography>
            Trainings:{' '}
            {trainingVersions.map((trainingVersion, index, array) => {
              return !trainingVersion.training?.task ? null : (
                <Fragment key={`${trainingVersion.id}-${index}`}>
                  <strong>{trainingVersion.training.task.key}</strong>
                  {index < array.length - 1 && ', '}
                </Fragment>
              );
            })}
          </Typography>
        )}

        <FormControlLabel
          label='Update related Training items'
          control={
            <Checkbox
              color='primary'
              // negate condition as the display message means to update training
              checked={!skipTrainingUpdates}
              onChange={(_, checked) => setSkipTrainingUpdates(!checked)}
            />
          }
        />
      </StyledDialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          color='primary'
          variant='contained'
          onClick={() => onSuccess?.({ skipTrainingUpdates })}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const CreateNewFileVersionConfigPlugin: TransitionConditionPlugin<CreateNewFileVersionConfigParams> =
  {
    type: 'create-new-file-version-config',
    label: 'Create New File Version Config',
    configurator: CreateNewFileVersionConfigConfigurator,
    display: CreateNewFileVersionConfigDisplay,
    validate: () => true,
    guard: (state: State<TaskContext>, params, render) => {
      console.log('guards: create-new-file-version-config', state, params);

      const pluginData = (state.context.pluginData =
        state.context.pluginData || {});

      const { task, taskEntity } = state.context;
      if (
        state.isSimulation ||
        // if not optional, no need to show dialog for the training updates skip
        !params.allowOptionalTrainingUpdates ||
        // skip if the targetEntity is available is but is not for File entities
        (task.taskType?.targetEntity && task.taskType.targetEntity !== 'File')
      ) {
        return true;
      }

      // set the result to pluginData
      return new Promise((resolve, reject) => {
        render?.(
          <CreateNewFileVersionDialog
            open
            // assume taskEntity is File entity
            file={taskEntity as FileDto | undefined}
            onSuccess={data => {
              pluginData.createNewFileVersionConfig = data;
              render?.(undefined);
              resolve(true);
            }}
            onCancel={() => {
              pluginData.createNewFileVersionConfig = undefined;
              render?.(undefined);
              reject(true);
            }}
          />
        );
      });
    }
  };
